import { type UseFormReturn } from 'react-hook-form';

import { RemoveShiftCaregiverButton } from '@apps/web/components/dashboard/remove_shift_caregiver_button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
} from '@apps/web/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { formatCaregiverName } from '@apps/web/lib/caregiver_name';
import {
    type Mode,
    type Shift,
    type ShiftStatus,
} from '@libraries/typescript/api_client';

const REMOVABLE_SHIFT_STATUSES: ReadonlySet<ShiftStatus> = new Set([
    'assigned',
    'assigned_and_confirmed',
    'pending_confirmation',
    'open',
]);

type ShiftCaregiverControlProps = {
    readonly shift: Shift;
    readonly caregivers:
        | ReadonlyArray<{ readonly id: string; readonly name: string }>
        | undefined;
    readonly caregiverForm: UseFormReturn<{ caregiver: string }>;
    readonly displayNamesLastFirst: boolean;
    readonly caregiverUnassignmentEnabled: boolean;
    readonly ehrName?: string;
    readonly mode: Mode | undefined;
    readonly readOnly: boolean;
    readonly onSetCaregiver: (
        shift: Shift,
        caregiverId: string,
        ehrName?: string,
    ) => Promise<void> | void;
};

export function ShiftCaregiverControl({
    shift,
    caregivers,
    caregiverForm,
    displayNamesLastFirst,
    caregiverUnassignmentEnabled,
    ehrName,
    mode,
    readOnly,
    onSetCaregiver,
}: ShiftCaregiverControlProps) {
    if (mode === 'sandbox' && !readOnly) {
        return (
            <Form {...caregiverForm}>
                <FormField
                    control={caregiverForm.control}
                    name="caregiver"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Select
                                    onValueChange={(value) => {
                                        field.onChange(value);
                                        onSetCaregiver(shift, value);
                                    }}
                                    value={
                                        shift.assigned_caregiver_id ??
                                        'unassigned'
                                    }
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select a caregiver" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="unassigned">
                                            Unassigned
                                        </SelectItem>
                                        {caregivers?.map((caregiver) => (
                                            <SelectItem
                                                key={caregiver.id}
                                                value={caregiver.id}
                                            >
                                                {formatCaregiverName(
                                                    caregiver.name,
                                                    displayNamesLastFirst,
                                                )}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                        </FormItem>
                    )}
                />
            </Form>
        );
    }

    if (!shift.assigned_caregiver_id) {
        return <p className="text-sm">Not assigned</p>;
    }

    const caregiverName = shift.caregiver?.name
        ? formatCaregiverName(shift.caregiver.name, displayNamesLastFirst)
        : 'Assigned caregiver';
    const canRemove =
        caregiverUnassignmentEnabled &&
        !readOnly &&
        REMOVABLE_SHIFT_STATUSES.has(shift.status);

    return (
        <div>
            <p className="text-sm">{caregiverName}</p>
            {canRemove ? (
                <RemoveShiftCaregiverButton
                    caregiverName={caregiverName}
                    ehrName={ehrName}
                    onRemove={() =>
                        onSetCaregiver(shift, 'unassigned', ehrName)
                    }
                />
            ) : null}
        </div>
    );
}
